import * as anchor from "@coral-xyz/anchor";
import { PublicKey } from "@solana/web3.js";
import House from "../house";
import GameSpec from "../gameSpec";
import { ZeebitV2 } from "../../target/types/zeebit_v2";
import PlayerToken from "../playerToken";
import GameInstanceSolo from "../gameInstance";
import { APP_NETWORK_TYPE } from "../../types/chain";

export default class Tower extends GameSpec {

  constructor(
    house: House,
    // hseTkn: HouseToken,
    gameSpecPubkey: PublicKey,
  ) {
    super(
      house,
      // hseTkn,
      gameSpecPubkey,
    )
    this.gameSpecPubkey = gameSpecPubkey;
  }
  static createNewInstance(
    game: GameSpec,
    gameSpecPubkey: PublicKey,
    playerToken: PlayerToken,
    prevInstance: TowerInstance,
    stateUpdate: any) {
    console.log('createNewState', { prevInstance, stateUpdate });
    console.log('state', stateUpdate);

    const newState = { stateUpdate }
    return new TowerInstance(
      game as Tower,
      gameSpecPubkey,
      playerToken,
      newState
    )
  }
  static async load(
    house: House,
    gameSpecPubkey: PublicKey,
  ) {
    const game = new Tower(
      house,
      gameSpecPubkey,
    )
    await game.loadAllState();
    return game
  }

  static async loadInstance(
    game: GameSpec,
    gameSpecPubkey: PublicKey,
    instancePubkey: PublicKey,
    playerToken: PlayerToken,
    baseInstanceState?: any,
    erInstanceState?: any,
  ) {

    const baseState = baseInstanceState || await game.loadInstanceState(instancePubkey);
    const erState = erInstanceState || await game.loadErInstanceState(instancePubkey);



    if (baseState || erState) {
      return new TowerInstance(
        game as Tower,
        gameSpecPubkey,
        playerToken,
        baseState,
        erState
      )
    }
    return null;
  }

  static loadInstanceFromBuffer(
    game: GameSpec,
    instancePubkey: PublicKey,
    playerToken: PlayerToken,
    baseInstanceBuffer?: any,
    erInstanceBuffer?: any,
  ) {

    const baseState = baseInstanceBuffer != null ? game.baseProgram.coder.accounts.decode('instanceSolo', baseInstanceBuffer): undefined;
    const erState = erInstanceBuffer != null ? game.erProgram.coder.accounts.decode('instanceSolo', erInstanceBuffer): undefined;

    if (baseState || erState) {
      return new TowerInstance(
        game as Tower,
        instancePubkey,
        playerToken,
        baseState,
        erState
      )
    }
    return null;
  }

  async soloBetIx(
    ownerOrAuth: PublicKey,
    playerToken: PlayerToken,
    inputs: {
      riskIdx: number,
      chosenStepIdx: number,
      wagerBasis: number,
      action?: {
        chosenStepIdx: number
      },
      insurance: boolean,
      identifier: PublicKey,
    },
    wager: number,
    clientSeed: number[],
  ) {
    const instanceRequest = {
      tower: { riskIdx: inputs.riskIdx },
    };

    const betRequests = [
      {
        tower: {
          chosenStepIdx: inputs.chosenStepIdx,
          wager: new anchor.BN(inputs.wagerBasis)
        }
      }
    ];

    const actionRequest = inputs.action ? {
      tower: {
        chosenStepIdx: inputs.action.chosenStepIdx
      }
    } : null;
    console.log({ actionRequest, betRequests, instanceRequest });

    const numberOfBets = 1;

    return await this.soloPlayIx(
      ownerOrAuth,
      playerToken,
      numberOfBets,
      instanceRequest,
      betRequests,
      actionRequest,
      clientSeed,
      inputs.identifier,
    );
  }

  get state() {
    // FALL BACK TO BASE STATE
    return this.isDelegated ? (this.erState || this.baseState): this.baseState;
  }

  get gameConfig() {
    return this.state ? this.state.config.blackjack : null;
  }

  get houseEdge(): number {
    if (this.state.config.tower) {
      return Number(this.state.config.tower.edgePerMillion) / 1_000_000
    } else {
      return undefined
    }
  }

  get maxMultiplier(): number {
    return 10 // TODO MAX MULTIPLIER
  }

  potentialPayout(
    wager: number
  ): number {
    return wager * this.maxMultiplier
  }

  getPayTable(riskLevel: number) {
    return this.state?.config?.tower?.payTables != null ? this.state.config.tower.payTables[riskLevel]: undefined
  }

  getBetMetas(bets: object[]) {
    let totalPayout = 0;
    let totalProfit = 0;
    let totalWager = 0;
    let edgeDollar = 0;
    let totalWagerBasis = 0;

    bets.forEach((bet) => {
      const riskLevel = bet.riskIdx
      const payTable = this.getPayTable(riskLevel)
      const multiplier = payTable.multiplierPerMillion / 1_000_000
      const edge = payTable.edgePerMillion / 1_000_000

      const payoutOnBet = multiplier * bet.wager;
      const probability = (1 - edge) / multiplier;

      // SET PAYOUT/PROBABILITY
      bet.payout = payoutOnBet;
      bet.probability = probability;
      bet.multiplier = multiplier;

      // INCREMENT METRICS
      totalPayout += payoutOnBet;
      totalProfit += payoutOnBet - bet.wager;
      totalWager += bet.wager;
      edgeDollar += edge * bet.wagerBasis;
      totalWagerBasis += bet.wagerBasis;
    });

    return {
      payout: totalPayout,
      profit: totalProfit,
      wager: totalWager,
      numberOfBets: bets.length,
      bets: bets,
      edgeDollar: edgeDollar,
      totalWagerBasis: totalWagerBasis,
      edgePercentage: edgeDollar / totalWagerBasis, // USED IN CALCULATING MAX BET VIA KELLY
    };
  }
}

// TOWER SPECIFIC LOGIC IN HERE
export class TowerInstance extends GameInstanceSolo {

  constructor(
    gameSpec: GameSpec,
    instancePubkey: PublicKey,
    playerToken: PlayerToken,
    chain: APP_NETWORK_TYPE,
    baseState: anchor.IdlAccounts<ZeebitV2>["instanceSolo"],
    erState: anchor.IdlAccounts<ZeebitV2>["instanceSolo"]
  ) {
    super(instancePubkey, gameSpec, playerToken, chain, baseState, erState)
  }

  static loadInstanceFromBuffer(
    game: GameSpec,
    instancePubkey: PublicKey,
    playerToken: PlayerToken,
    chain: APP_NETWORK_TYPE,
    baseInstanceBuffer?: any,
    erInstanceBuffer?: any,
  ) {
    const baseState = baseInstanceBuffer != null ? game.baseProgram.coder.accounts.decode('instanceSolo', baseInstanceBuffer): undefined
    const erState = erInstanceBuffer != null ? game.erProgram.coder.accounts.decode('instanceSolo', erInstanceBuffer): undefined

    if (baseState || erState) {
      return new TowerInstance(
        game as Tower,
        instancePubkey,
        playerToken,
        chain,
        baseState,
        erState
      )
    }
    return null;
  }

  get isPlayersTurn(): boolean {
    return this.state && (Object.keys(this.state.status)[0] == "awaitingPlayerResponse") ? true : false
  }

  get hasSate() {
    return this.state != null
  }
}





